import { BookmarkButton } from '@mdvip/shared/components/blocks/BookmarkButton';
import { Icon, type IconName } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';
import * as React from 'react';
import { Link } from 'react-router-dom';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export interface ContentCardMeta {
  icon: IconName;
  label: string;
}

export interface ContentCardProps {
  kind?: string;
  orientation?: 'vertical' | 'row';
  title: string;
  description?: string;
  meta?: ContentCardMeta[];
  imageSrc?: string;
  imagePlaceholder?: string;
  href: string;
  savable?: boolean;
  saved?: boolean;
  onSaveChange?: (saved: boolean) => void;
  className?: string;
}

/**
 * ContentCard — one card shape for every piece of library content (recipes, articles, plans). The
 * whole surface is clickable via a link overlay on the title, so assistive tech sees exactly one
 * link per card while the save toggle stays independently reachable.
 */
export function ContentCard({
  kind = 'article',
  orientation = 'vertical',
  title,
  description,
  meta = [],
  imageSrc,
  imagePlaceholder,
  href,
  savable = false,
  saved = false,
  onSaveChange,
  className,
}: ContentCardProps) {
  const row = orientation === 'row';
  const image = imageSrc || imagePlaceholder;
  const titleId = React.useId();

  const saveButton = savable ? (
    <BookmarkButton variant="icon" saved={saved} onSaveChange={(next) => onSaveChange?.(next)} itemTitle={title} />
  ) : null;

  return (
    <article
      data-slot="content-card"
      data-kind={kind}
      aria-labelledby={titleId}
      className={cn(
        '@container group relative flex overflow-hidden rounded-xl border bg-card shadow-sm transition-[transform,box-shadow,border-color] duration-normal ease-out hover:border-hairline-brand hover:shadow-md motion-safe:hover:-translate-y-0.5',
        row ? 'flex-row' : 'flex-col',
        className,
      )}
    >
      {image && (
        <div
          className={cn(
            'relative shrink-0 overflow-hidden bg-muted',
            row ? 'w-28 border-r @[520px]:w-44' : 'aspect-[16/9] border-b',
          )}
        >
          <img src={image} alt="" className="size-full object-cover" />
          {saveButton && <div className="absolute right-2 top-2 z-10">{saveButton}</div>}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-4 @[520px]:p-5">
        {!image && saveButton && (
          <div className="flex justify-end">
            <div className="relative z-10 -mr-1 -mt-1">{saveButton}</div>
          </div>
        )}
        <h3
          id={titleId}
          className={cn('font-semibold leading-snug text-foreground', row ? 'text-base' : 'text-base @[520px]:text-lg')}
        >
          <Link to={href} className={cn("after:absolute after:inset-0 after:content-['']", FOCUS)}>
            {title}
          </Link>
        </h3>
        {description && (
          <p className={cn('text-sm leading-relaxed text-muted-foreground', row ? 'line-clamp-2' : 'line-clamp-3')}>
            {description}
          </p>
        )}
        {meta.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {meta.map((m) => (
              <span key={m.label} className="flex items-center gap-1.5">
                <Icon name={m.icon} size="xs" />
                {m.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
